import { useCallback, useState } from "react";
import { aggregationFromLabel } from "../lib/search/formatAggregationLabel";
import { phraseAlignment } from "../lib/search/phraseMatch";
import { placeFragmentOf } from "../lib/search/placeFragment";

/**
 * The search bar's core state machine: an array of segments, `{type: "text", value, id}` or
 * `{type: "token", id, display, category, sourceType, payload, siblings}`, per
 * IMPLEMENTATION_CHECKLIST.md's data model. Kept as plain, renderer-free logic so it's testable
 * without mounting anything -- the DOM/rendering piece (TokenizedSearchInput) just calls these.
 *
 * The trailing segment is always text -- what's currently being typed. Matching/committing
 * operate on its LAST WORD only ("... among fema" matches "fema", leaving " among " as plain
 * text) -- `activeFragment` is that last word, `activeText` the full trailing value.
 *
 * Place matching uses a wider window (`activePlaceFragment`, see placeFragment.js): most real place
 * names are multi-word ("New York", "Pinellas County Florida"), so just the last word misranks
 * them, but the full `activeText` over-dilutes once filler words precede the place. Term/filter
 * matching is unaffected and keeps using `activeFragment`.
 *
 * Tokens are addressed by their own segment id everywhere (update/remove), never by position --
 * a token stays clickable/editable for its whole lifetime, not just the most recent one.
 *
 * The ACTIVE text segment -- typed into and matched against -- defaults to the trailing one, but
 * isn't always it: every text segment stays a real, clickable cursor position for its whole
 * lifetime. `activateSegment` moves the active position to any of them, and
 * `commitToken`/`setActiveFragment` splice in place rather than always appending at the end.
 * `activeSegmentId` is `null` until something explicitly activates a different segment, meaning
 * "the trailing one" -- every existing tail-typing caller keeps its behavior unchanged by default.
 *
 * A reactivated segment can have real content on BOTH sides of the cursor (e.g. clicking back
 * into a gap and typing before existing tail text), so `activeFragment`/`activePlaceFragment` are
 * derived from the text BEFORE the cursor, and `commitToken` preserves whatever's AFTER it as its
 * own segment. `setActiveFragment(value, cursorPos)`'s `cursorPos` defaults to `value.length`
 * (the end) when omitted, so a caller that doesn't know about cursors keeps the old
 * trailing-only behavior.
 */

let nextId = 0;
const makeTextSegment = (value = "") => ({ type: "text", value, id: `text-${nextId++}` });
const makeTokenSegment = (term) => ({
  type: "token",
  id: `token-${nextId++}`,
  termId: term.id,
  display: term.display,
  // Stable across later `display` updates (e.g. a continuous metric's band-selector rewriting
  // "Local Voter Score" -> "Local Voter Score of 41-60, 61-80") -- BandSelector's config panel
  // needs the plain metric name back, not whatever `display` currently reads. `resetToSegments`
  // passes this explicitly for a resolved-but-overridden pill (e.g. "Local Voter Score by
  // Average"), whose real base display would otherwise be lost the same way; everything else
  // (a freshly-committed term) has no such override, so it falls back to `term.display`.
  originalDisplay: term.originalDisplay ?? term.display,
  category: term.category,
  sourceType: term.sourceType,
  payload: term.feature ?? term.payload ?? null,
  siblings: term.siblings ?? [],
  // Set only when a term arrives with a known-good initial selection (see resetToSegments' Age
  // custom-range parsing) -- BandSelector reads this to restore state instead of defaulting to
  // "All" on open. Absent for a freshly-typed/committed term, which has no selection yet.
  bandSelection: term.bandSelection,
});

/** Splits "... among fema" into { precedingText: "... among ", word: "fema" }. */
function splitLastWord(text) {
  const match = text.match(/^(.*?)(\S*)$/s);
  return { precedingText: match?.[1] ?? "", word: match?.[2] ?? "" };
}

export function leadingPhraseStart(text, display) {
  return phraseAlignment(text, display)?.start ?? null;
}

export function useSegments() {
  const [segments, setSegments] = useState(() => [makeTextSegment()]);
  // `null` means "the trailing segment" -- see the module docstring. Explicit only once
  // `activateSegment` has pointed it somewhere else.
  const [explicitActiveId, setExplicitActiveId] = useState(null);
  // Cursor position WITHIN the active segment's own value. `null` means "the end of whatever it
  // currently is" -- every caller that never passes a cursor position keeps that behavior
  // (identical to how this worked before cursor-awareness existed).
  const [cursorOffset, setCursorOffset] = useState(null);

  const activeIndex =
    explicitActiveId != null
      ? (() => {
          const idx = segments.findIndex((s) => s.id === explicitActiveId);
          return idx === -1 ? segments.length - 1 : idx;
        })()
      : segments.length - 1;
  const activeSegment = segments[activeIndex];
  const activeText = activeSegment?.type === "text" ? activeSegment.value : "";
  // Clamped defensively: a stale offset from a previous, longer value should never index past
  // the current one (e.g. right after an external reset).
  const effectiveCursor = Math.max(0, Math.min(cursorOffset ?? activeText.length, activeText.length));
  const textBeforeCursor = activeText.slice(0, effectiveCursor);
  const { word: activeFragment } = splitLastWord(textBeforeCursor);
  const activePlaceFragment = placeFragmentOf(textBeforeCursor);

  // Not memoized: needs a fresh read of `explicitActiveId` every call, and nothing relies on
  // this function's identity being stable across renders.
  const setActiveFragment = (value, cursorPos) => {
    setSegments((prev) => {
      const idx =
        explicitActiveId != null
          ? (() => {
              const found = prev.findIndex((s) => s.id === explicitActiveId);
              return found === -1 ? prev.length - 1 : found;
            })()
          : prev.length - 1;
      const target = prev[idx];
      if (target?.type === "text") {
        return [...prev.slice(0, idx), { ...target, value }, ...prev.slice(idx + 1)];
      }
      return [...prev, makeTextSegment(value)];
    });
    setCursorOffset(cursorPos ?? value.length);
  };

  /**
   * Replaces just the word ending at the CURSOR with a committed token -- text before that word
   * stays as its own frozen segment, and so does any text AFTER the cursor (preserved, not
   * discarded, for mid-string edits). Splices a fresh empty text segment right after the token
   * and moves the active position there, so typing continues where the token landed. Returns the
   * new segment's id synchronously so the caller can open its config step without a re-render.
   */
  const commitToken = (term) => {
    const segment = makeTokenSegment(term);
    const nextEmpty = makeTextSegment();
    setSegments((prev) => {
      const idx =
        explicitActiveId != null
          ? (() => {
              const found = prev.findIndex((s) => s.id === explicitActiveId);
              return found === -1 ? prev.length - 1 : found;
            })()
          : prev.length - 1;
      const active = prev[idx];
      const fullValue = active?.type === "text" ? active.value : "";
      const cursor = Math.max(0, Math.min(cursorOffset ?? fullValue.length, fullValue.length));
      // A suggestion carries the span it covers; replace all of it, not just the last word.
      const span = term.span && term.span.end <= cursor ? term.span : null;
      const phraseStart = span ? null : leadingPhraseStart(fullValue.slice(0, cursor), term.display);
      const precedingText = span
        ? fullValue.slice(0, span.start)
        : phraseStart != null
        ? fullValue.slice(0, phraseStart)
        : splitLastWord(fullValue.slice(0, cursor)).precedingText;
      const suffixText = fullValue.slice(cursor);
      // Whitespace-only preceding/suffix text isn't worth its own segment -- the row's own
      // `gap: 4` already provides that visual separation, and flattenedText inserts a space at
      // any boundary that needs one.
      const precedingSegment = precedingText.trim() ? [makeTextSegment(precedingText)] : [];
      const suffixSegment = suffixText.trim() ? [makeTextSegment(suffixText)] : [];
      return [
        ...prev.slice(0, idx),
        ...precedingSegment,
        segment,
        nextEmpty,
        ...suffixSegment,
        ...prev.slice(idx + 1),
      ];
    });
    setExplicitActiveId(nextEmpty.id);
    setCursorOffset(null);
    return segment.id;
  };

  /** Updates a token by its own segment id -- works for any token, not just the most recent. */
  const updateToken = useCallback((id, updates) => {
    setSegments((prev) =>
      prev.map((s) => (s.type === "token" && s.id === id ? { ...s, ...updates } : s))
    );
  }, []);

  /**
   * Removes a token by id and merges its two neighboring text segments into one, so backspacing
   * through a removed token doesn't leave a stray empty segment sitting in the middle. If the
   * active position was one of those two neighbors, it moves to the merged segment instead of
   * silently falling back to the trailing one -- otherwise removing a token you were actively
   * typing next to would yank focus away to the far end of the bar.
   */
  const removeToken = (id) => {
    const idx = segments.findIndex((s) => s.type === "token" && s.id === id);
    if (idx === -1) return;

    const before = segments[idx - 1];
    const after = segments[idx + 1];
    const wasActive = explicitActiveId === before?.id || explicitActiveId === after?.id;
    const mergedValue =
      (before?.type === "text" ? before.value : "") + (after?.type === "text" ? after.value : "");
    // Same reasoning as activateSegment's own trailing-space fix: this merged segment can become
    // the active one below, immediately ready for more typing -- if its value doesn't already
    // end in whitespace, that typing would glue onto its last word instead of starting a fresh
    // one, same "mailmarried" bug either way.
    const merged = makeTextSegment(
      wasActive && mergedValue && !/\s$/.test(mergedValue) ? `${mergedValue} ` : mergedValue
    );
    const start = before?.type === "text" ? idx - 1 : idx;
    const end = after?.type === "text" ? idx + 1 : idx;

    setSegments((prev) => [...prev.slice(0, start), merged, ...prev.slice(end + 1)]);
    if (wasActive) {
      setExplicitActiveId(merged.id);
      setCursorOffset(null);
    }
  };

  /**
   * Moves the active (typed-into/matched-against) position to an existing text segment -- the
   * fix for "I should be able to click and adjust parts at any time": every plain-text span in
   * the bar, not just the trailing one, is a real cursor position for its whole lifetime. A
   * no-op for a token id (tokens are edited via their own config step, not by becoming text).
   */
  // A click can't preserve a real caret offset across a <span> -> <input> swap, so activation
  // always lands the cursor at the end of the existing text. If that text doesn't already end in
  // whitespace, appending one (once, only when needed) stops whatever's typed next from gluing
  // onto its last word (e.g. "mail" + "married" -> "mailmarried", matching nothing).
  const activateSegment = (id) => {
    const target = segments.find((s) => s.id === id);
    if (target?.type !== "text") return;
    // An abandoned, genuinely empty text segment (a commit's leftover slot, never typed into) is
    // dead weight -- nothing else ever reactivates or merges it away, so it's dropped here unless
    // it's the trailing segment (the bar must always end in a real, typeable position).
    const previousActiveId = activeSegment?.id;
    setExplicitActiveId(id);
    setCursorOffset(null);
    setSegments((prev) => {
      const withGuard =
        target.value && !/\s$/.test(target.value)
          ? prev.map((s) => (s.id === id ? { ...s, value: `${s.value} ` } : s))
          : prev;
      if (previousActiveId && previousActiveId !== id) {
        const prevIdx = withGuard.findIndex((s) => s.id === previousActiveId);
        const isAbandonedEmpty =
          prevIdx !== -1 &&
          prevIdx !== withGuard.length - 1 &&
          withGuard[prevIdx].type === "text" &&
          withGuard[prevIdx].value === "";
        if (isAbandonedEmpty) {
          return [...withGuard.slice(0, prevIdx), ...withGuard.slice(prevIdx + 1)];
        }
      }
      return withGuard;
    });
  };

  /**
   * The token immediately before the active text segment, when that segment's FULL raw value is
   * empty (not just its last word) -- i.e. the cursor sits right after the token with nothing
   * typed since. This is the backspace-removes-the-whole-token case, distinct from mid-word
   * matching above.
   */
  const previousToken = (() => {
    if (activeText !== "") return null;
    const candidate = segments[activeIndex - 1];
    return candidate?.type === "token" ? candidate : null;
  })();

  const clear = useCallback(() => {
    setSegments([makeTextSegment()]);
    setExplicitActiveId(null);
    setCursorOffset(null);
  }, []);

  /**
   * Resets to a single plain-text segment carrying `value` -- for seeding the bar from an
   * external plain-string source (Redux `state.search.query`, written by a template click or a
   * Data Catalog selection) rather than from typing. Doesn't attempt to tokenize `value`; it's
   * just shown as plain text, same as it would be if the user had typed it.
   */
  const resetToText = useCallback((value = "") => {
    setSegments([makeTextSegment(value)]);
    setExplicitActiveId(null);
    setCursorOffset(null);
  }, []);

  /**
   * Resets to a sequence built from raw template segments (`string | {pill: string}`, the same
   * shape HighlightedText/TemplateCard render read-only) -- for when a template is clicked and
   * its pills need to land as *real*, editable tokens. Each pill is resolved against `termIndex`
   * by exact match on its text, so it gets genuine siblings/config capability; an unmatched pill
   * still becomes a token (visible, removable) but with no siblings/config step.
   *
   * A pill's text can also be a pre-composed Age band display ("Ages 25-40") rather than the
   * atomic "Age" term's own display -- that composed text is never indexable, so it falls back to
   * the real Age term (by `feature.type === "age"`) whenever the pill reads like Age's own
   * compose format, keeping the pill's own text on screen while giving it a real config step.
   */
  const resetToSegments = useCallback((rawSegments = [], termIndex = []) => {
    const built = [];
    let textBuffer = "";

    const flushTextBuffer = () => {
      if (textBuffer) built.push(makeTextSegment(textBuffer));
      textBuffer = "";
    };

    rawSegments.forEach((piece) => {
      if (typeof piece === "string") {
        textBuffer += piece;
        return;
      }
      flushTextBuffer();
      const pillText = piece.pill;
      const looksLikeAgeBandDisplay =
        pillText === "All Ages" || /^Ages\s/i.test(pillText);
      // A continuous metric's composed text is "{display} by {Aggregation label}" (see
      // parametersToSegments.js and BandSelector's own `showsAggregation` composition) -- the
      // base display alone won't exact-match termIndex, so without this fallback a token like
      // "Local Voter Score by Average" fell through to "unresolved" and couldn't be reopened at
      // all (only Age had an equivalent fallback). Matched against `continuous` terms
      // specifically so a metric whose display text coincidentally contains " by " isn't misread.
      const aggregationMatch = pillText.match(/^(.+) by (.+)$/);
      const resolved =
        termIndex.find((term) => term.display.toLowerCase() === pillText.toLowerCase()) ??
        (looksLikeAgeBandDisplay
          ? termIndex.find((term) => term.feature?.type === "age")
          : undefined) ??
        (aggregationMatch
          ? termIndex.find(
              (term) =>
                term.feature?.type === "continuous" &&
                term.display.toLowerCase() === aggregationMatch[1].toLowerCase()
            )
          : undefined);
      // Restoring the parsed bands as the initial selection means BandSelector shows them on
      // first open instead of defaulting to "All Ages". Comma-separated ("Ages 18-24, 25-34", a
      // Data Catalog multi-select) restores as `selectedPresets`; a single "min-max" span
      // restores as a `customRange`.
      const bandsMatch = pillText.match(/^Ages\s+(.+)$/i);
      const bandSelection = bandsMatch
        ? (() => {
            const bands = bandsMatch[1].split(",").map((b) => b.trim());
            const singleRangeMatch = bands.length === 1 && bands[0].match(/^(\d+)-(\d+)$/);
            return singleRangeMatch
              ? { isAll: false, selectedPresets: [], customRange: `${singleRangeMatch[1]}-${singleRangeMatch[2]}` }
              : { isAll: false, selectedPresets: bands, customRange: null };
          })()
        : aggregationMatch
        ? {
            isAll: true,
            selectedPresets: [],
            customRange: null,
            aggregation: aggregationFromLabel(aggregationMatch[2]),
          }
        : undefined;
      // Override display so the chip keeps showing the template's own band text ("Ages 25-40"),
      // not the Age-fallback match's generic "Age"/"All Ages" display.
      const resolvedTerm =
        resolved && resolved.display.toLowerCase() !== pillText.toLowerCase()
          ? { ...resolved, display: pillText, originalDisplay: resolved.display, bandSelection }
          : resolved;
      built.push(
        makeTokenSegment(
          resolvedTerm ?? {
            id: `unresolved:${pillText}`,
            display: pillText,
            sourceType: "unresolved",
          }
        )
      );
    });
    // Real trailing text stays live and editable here (not frozen with a separate empty segment
    // tacked on), consistent with every other text segment in this file -- a pill-ending (or
    // empty) input still needs a fresh slot, since there's nothing real to land in otherwise.
    if (textBuffer) {
      built.push(makeTextSegment(textBuffer));
    } else {
      built.push(makeTextSegment());
    }

    setSegments(built);
    setExplicitActiveId(null);
    setCursorOffset(null);
  }, []);

  // Committing a token refocuses the input on a fresh empty segment; typing straight into it with
  // no leading space ("among fema" right after a click, no space typed) would otherwise run the
  // token's own display text straight into it ("Local Voter Scoreamong"). General rule instead of
  // enumerating each adjacency: insert a space at any boundary where neither side already has
  // real whitespace, regardless of which side is a token.
  const flattenedText = segments.reduce((acc, segment) => {
    const piece = segment.type === "token" ? segment.display : segment.value;
    if (!piece) return acc;
    const needsSpace = acc.length > 0 && !/\s$/.test(acc) && !/^\s/.test(piece);
    return acc + (needsSpace ? " " : "") + piece;
  }, "");

  // The same string|{pill} shape templates.js/resetToSegments use -- exposed so a caller
  // (SearchBar's submit handler) can persist exactly what was typed/committed, not a
  // re-derivation of the flattened string through the backend's own parse.
  const templateSegments = segments
    .map((segment) => (segment.type === "token" ? { pill: segment.display } : segment.value))
    .filter((piece) => piece !== "");

  return {
    segments,
    // Resolved -- always a real segment id, generalized beyond "the last one" (see the module
    // docstring). Consumers use this to know which segment renders as the live input.
    activeSegmentId: activeSegment?.id,
    activeText,
    textBeforeCursor,
    activeFragment,
    activePlaceFragment,
    previousToken,
    setActiveFragment,
    commitToken,
    updateToken,
    removeToken,
    activateSegment,
    clear,
    resetToText,
    resetToSegments,
    flattenedText,
    templateSegments,
  };
}
